/**
 * "Commit agent config to your repo" (T098). For teams that keep agent config
 * in the website's repository: a Claude Code skill, an AGENTS.md section for
 * other agents, and a `.mcp.json` entry that reads the token from an
 * environment variable. The server renders the files
 * (`GET /partner/agent-setup-bundle`) from the same playbook as the MCP
 * server's instructions, so they cannot drift. Nothing in them is secret; the
 * warning is about the token, which never goes in the repo.
 *
 * Offered only on the docs page (/docs/ai-setup), to a signed-in visitor
 * (owner decision 2026-09-27: not on AI agent access or the Install step).
 */
import type { AgentSetupBundle, AgentSetupBundleFile } from '../api/types';
import { useSession } from '../auth/session';
import { CodeBlock, Disclosure, ErrorNotice, Loading } from '../ui/kit';
import { useQuery } from '../ui/hooks';
import { TOKEN_ENV_VAR } from './handoff';
import { zipFiles } from './zip';

export const BUNDLE_ZIP_NAME = 'finderlyfix-agent-config.zip';

/** What the bundle contains, for the docs (the server's PLAYBOOK_FILES; agent.test.tsx checks them). */
export const BUNDLE_FILES: ReadonlyArray<[string, string]> = [
  ['.claude/skills/finderlyfix-setup/SKILL.md', 'A Claude Code skill with the setup playbook. Claude Code loads it when someone asks it to set up FinderlyFix.'],
  ['AGENTS.md', 'The same playbook as a section for other coding agents. Append it to an existing AGENTS.md.'],
  ['.mcp.json', `Registers the finderlyfix MCP server for the project. The token is a \${${TOKEN_ENV_VAR}} reference, never the value.`],
  ['finderlyfix-agent-setup.md', 'A short note for teammates: where the token goes (their shell or secret manager) and where it never goes.'],
];

const MERGE_HINT: Record<AgentSetupBundleFile['merge'], string> = {
  replace: 'New file',
  'append-section': 'Add this section to your AGENTS.md (create it if you have none)',
  'merge-json': 'Add the finderlyfix entry under mcpServers if you already have a .mcp.json',
};

function download(bundle: AgentSetupBundle) {
  const bytes = zipFiles(bundle.files);
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = BUNDLE_ZIP_NAME;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function Files() {
  const { api } = useSession();
  const bundle = useQuery(() => api.getAgentSetupBundle(), []);
  if (bundle.loading) return <Loading />;
  if (bundle.error || !bundle.data) return <ErrorNotice error={bundle.error} />;
  const data = bundle.data;
  return (
    <div className="stack">
      <div className="row">
        <button type="button" className="btn btn-sm" onClick={() => download(data)}>
          Download all (.zip)
        </button>
        <span className="small muted">Unzip at the repository root; the folders are included.</span>
      </div>
      {data.files.map((f) => (
        <div key={f.path} className="stack-sm">
          <CodeBlock caption={f.path} code={f.content} label={f.path} />
          <span className="small muted">{MERGE_HINT[f.merge]}</span>
        </div>
      ))}
    </div>
  );
}

/** The files behind a "Show files" disclosure; fetched on first open only. */
export function RepoAgentConfig() {
  return (
    <Disclosure label="Show files">
      <Files />
    </Disclosure>
  );
}
